import { z } from 'zod';
import {
  retailerSparkFiltersSchema,
  sparkAwardSchema,
  SPARK_FULFILLMENT_TYPES,
} from '../spark/spark.core';
import { objectIdRegex } from '../util/regex';
import { AccountMarkets } from '../account';
import { bulkSparksModeSchema, BULK_MASTER_SPARK_OMITTED_PROPERTIES } from './bulkSparks.zod';

/**
 * Market-wide sparks payloads for the Spark Wizard.
 *
 * Market-wide sparks are always leaderboards with a SparkBundle linking
 * multiple retailer sparks into a single unified leaderboard.
 *
 * Key differences from bulk sparks:
 * - type and detailedSparkType are rigid (always 'leaderboard')
 * - awards and fulfillmentTypes are required
 * - No commission fields (leaderboards don't have commissions)
 * - Response includes required bundleId
 */

/**
 * Master spark schema with rigid leaderboard constraints.
 * No commission fields - market-wide leaderboards don't support commissions.
 */
export const marketWideMasterSparkSchema = z
  .object({
    originatorGroupId: z.string().regex(objectIdRegex),
    sparkBrandId: z.string().regex(objectIdRegex),
    markets: z.array(z.enum(AccountMarkets as [string, ...string[]])).min(1),

    name: z.string().min(1),
    description: z.string().optional(),
    emoji: z.string().optional(),

    // Rigid leaderboard type
    type: z.literal('leaderboard'),
    detailedSparkType: z.literal('leaderboard'),
    metric: z.string().min(1),

    startDate: z.string().min(1),
    endDate: z.string().min(1),

    // Required for leaderboards
    awards: z.array(sparkAwardSchema).min(1),
    fulfillmentTypes: z.array(z.enum(SPARK_FULFILLMENT_TYPES)).min(1),

    // Optional leaderboard fields
    minimumThresholdToQualify: z.number().optional(),
    minimumTransactionsToQualify: z.number().optional(),
    splitPayout: z.boolean().default(false),
  })
  .passthrough()
  .superRefine((obj, ctx) => {
    // Guardrails: ensure we never accidentally embed per-retailer fields into masterSpark.
    BULK_MASTER_SPARK_OMITTED_PROPERTIES.forEach((key) => {
      if (key in obj) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: `masterSpark must not include '${key}' (it is derived per retailer or not used in market-wide)`,
        });
      }
    });
  });
export type MarketWideMasterSpark = z.infer<typeof marketWideMasterSparkSchema>;

/**
 * Per-retailer spec for market-wide sparks.
 * No commission fields - market-wide leaderboards don't support commissions.
 */
export const marketWideRetailerSpecSchema = z.object({
  retailerAccountId: z.string().min(1),
  locationIds: z.array(z.string().min(1)).min(1),
  retailerFilters: retailerSparkFiltersSchema,
  minimumThresholdToQualify: z.number().optional(),
});
export type MarketWideRetailerSpec = z.infer<typeof marketWideRetailerSpecSchema>;

/**
 * Request body for market-wide sparks endpoint.
 */
export const marketWideSparksRequestBodySchema = z.object({
  mode: bulkSparksModeSchema,
  masterSpark: marketWideMasterSparkSchema,
  retailers: z.array(marketWideRetailerSpecSchema).min(1),
});
export type MarketWideSparksRequestBody = z.infer<typeof marketWideSparksRequestBodySchema>;

/**
 * Response body for market-wide sparks endpoint.
 * Includes required bundleId since market-wide sparks always create a bundle.
 */
export const marketWideSparksResponseBodySchema = z.object({
  ok: z.literal(true),
  sparkIds: z.array(z.string().min(1)),
  byRetailer: z.record(z.string().min(1), z.string().min(1)),
  batchId: z.string().min(1),
  bundleId: z.string().min(1),
});
export type MarketWideSparksResponseBody = z.infer<typeof marketWideSparksResponseBodySchema>;
